import { useState } from 'react'
import { FileRejection } from 'react-dropzone'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { theme } from '../../theme/theme'
import { BodySmall } from '../styles/Typography'
import { Close, ErrorSign, Paper } from '../icons'
import { DropZone } from '../inputs/DropZone'
import {
  CERTIFICATION_DOCUMENT_ACCEPTED_FILES,
  CERTIFICATION_DOCUMENT_MAX_BYTES,
  CERTIFICATION_DOCUMENT_MAX_FILES,
} from '../../api/ester-b2b-api/private/certification/types'

const BYTES_PER_MEGABYTE = 1024 * 1024

const withoutDuplicateNames = (files: File[]): File[] =>
  files.filter((file, index) => files.findIndex((other) => other.name === file.name) === index)

// react-dropzone turns a whole drop away when it holds more files than the cap allows, so this
// code can arrive either from the drop zone or from accumulating attachments across several drops.
const TOO_MANY_FILES = 'too-many-files'

type Props = {
  documents: File[]
  onChange: (documents: File[]) => void
}

export const PolicyDocumentUpload = ({ documents, onChange }: Props) => {
  const { t } = useTranslation()
  const [rejectionMessage, setRejectionMessage] = useState<string | null>(null)

  const rejectionMessageFor = (code: string): string => {
    switch (code) {
      case TOO_MANY_FILES:
        return t('admin.certification.uploadTooManyFiles', { maxFiles: CERTIFICATION_DOCUMENT_MAX_FILES })
      case 'file-too-large':
        return t('admin.certification.uploadFileTooLarge', {
          maxSize: CERTIFICATION_DOCUMENT_MAX_BYTES / BYTES_PER_MEGABYTE,
        })
      case 'file-invalid-type':
        return t('admin.certification.uploadWrongFileType')
      default:
        return t('admin.certification.uploadRejected')
    }
  }

  const addDocuments = (droppedFiles: File[], rejections: FileRejection[]) => {
    if (droppedFiles.length === 0 && rejections.length === 0) return

    const combined = withoutDuplicateNames([...documents, ...droppedFiles])
    const accepted = combined.slice(0, CERTIFICATION_DOCUMENT_MAX_FILES)
    const rejectionCode = rejections[0]?.errors[0]?.code ?? (accepted.length < combined.length ? TOO_MANY_FILES : null)

    setRejectionMessage(rejectionCode ? rejectionMessageFor(rejectionCode) : null)
    onChange(accepted)
  }

  const removeDocument = (name: string) => {
    setRejectionMessage(null)
    onChange(documents.filter((file) => file.name !== name))
  }

  return (
    <Container>
      <DropZone
        acceptedFiles={CERTIFICATION_DOCUMENT_ACCEPTED_FILES}
        onDrop={addDocuments}
        maxFiles={CERTIFICATION_DOCUMENT_MAX_FILES}
        maxSize={CERTIFICATION_DOCUMENT_MAX_BYTES}
      />
      {documents.length > 0 && (
        <DocumentList>
          {documents.map((file) => (
            <DocumentRow key={file.name}>
              <Paper size={16} color={theme.color.plum} />
              <DocumentName>{file.name}</DocumentName>
              <RemoveButton
                type="button"
                onClick={() => removeDocument(file.name)}
                aria-label={t('admin.certification.removeDocument', { fileName: file.name })}
              >
                <Close size={16} color={theme.color.plum} />
              </RemoveButton>
            </DocumentRow>
          ))}
        </DocumentList>
      )}
      {rejectionMessage && (
        <RejectionMessage>
          <ErrorSign size={20} color={theme.color.red} />
          <BodySmall>{rejectionMessage}</BodySmall>
        </RejectionMessage>
      )}
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.small}px;
  margin-top: ${theme.spacing.mediumLarge}px;
`

const DocumentList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xsmall}px;
  list-style: none;
  margin: 0;
  padding: 0;
  max-width: 416px;
`

const DocumentRow = styled.li`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xsmall}px;
  background: ${theme.color.white};
  border-radius: 8px;
  padding: ${theme.spacing.xsmall}px ${theme.spacing.small}px;
`

const DocumentName = styled(BodySmall)`
  flex: 1;
  overflow-wrap: anywhere;
`

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
`

const RejectionMessage = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xsmall}px;
  max-width: 416px;
`
